import React, { useEffect, useState } from "react";
import TableComponent from "../../../src/components/table/tableComponent";
import API_BASE_URL from "../config";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClassesThunk,
  fetchSubjectsByClassThunk,
  fetchSectionsBySubjectThunk,
} from "../chapter/redux/chapterThunk";
import {
  setSelectedClassId,
  setSelectedSubjectId,
  // setSelectedSectionId, // not used here
} from "../chapter/redux/chapterSlice";


const SectionManager = () => {
  const dispatch = useDispatch();
  const {
    classes,
    subjects,
    sections,
    selectedClassId,
    selectedSubjectId,
    loadingSubjects,
    loadingSections,
  } = useSelector((state) => state.chapter);

  // local state for section add/edit form
  const [sectionName, setSectionName] = useState("");
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchClassesThunk());
  }, [dispatch]);

  useEffect(() => {
    if (selectedClassId) {
      dispatch(fetchSubjectsByClassThunk(selectedClassId));
      // reset subject when class changes
      dispatch(setSelectedSubjectId(""));
      localStorage.removeItem("selectedSectionId");
    }
  }, [dispatch, selectedClassId]);

  useEffect(() => {
    if (selectedClassId && selectedSubjectId) {
      dispatch(fetchSectionsBySubjectThunk({ subjectId: selectedSubjectId }));
      localStorage.removeItem("selectedSectionId");
    }
  }, [dispatch, selectedClassId, selectedSubjectId]);

  // Create or Update section via API
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!sectionName?.trim() || !selectedClassId || !selectedSubjectId) {
      alert("Please enter section name and select class & subject.");
      return;
    }
   
    const payload = {
      section: sectionName.trim(),
      subject_id: Number(selectedSubjectId),
    };

    setSaving(true);
    try {
      let res;
      if (editingSection) {
        // Update: PUT /api/sections/:id
        res = await fetch(`${API_BASE_URL}/subjects/update-section/${editingSection.section_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        // Create: POST /api/sections
        res = await fetch(`${API_BASE_URL}/subjects/create-section`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(errText || `API error ${res.status}`);
      }

      // success -> refresh list
      dispatch(fetchSectionsBySubjectThunk({ subjectId: selectedSubjectId }));
      setSectionName("");
      setEditingSection(null);
    } catch (err) {
      console.error("Save section failed:", err);
      alert("Failed to save section: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  // Delete section
  const handleDelete = async (sectionId) => {
    if (!window.confirm("Are you sure you want to delete this section?")) return;

    try {
      const res = await fetch(`${API_BASE_URL}/subjects/delete-section/${sectionId}`, { method: "DELETE" });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Delete failed: ${res.status}`);
      }
      dispatch(fetchSectionsBySubjectThunk({ subjectId: selectedSubjectId }));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete section: " + (err.message || "Unknown error"));
    }
  };

  // Table columns
  const tableColumns = [
    { header: "#", accessor: "index" },
    { header: "Section Name", accessor: "section_name" },
    { header: "Actions", accessor: "actions" },
  ];

  const processSections = (sectionList) =>
    (sectionList || []).map((sec, index) => ({
      index: index + 1,
      section_name: sec.section_name,
      actions: (
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => {
              // populate form for editing
              setEditingSection(sec);
              setSectionName(sec.section_name);
              // ensure subject/class match the section (optional)
              // if sec.subject_id exists and differs, you could dispatch setSelectedSubjectId(sec.subject_id)
            }}
            className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500"
          >
            Edit
          </button>

          <button
            onClick={() => handleDelete(sec.section_id)}
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      ),
    }));

  return (
    <div className="container-fluid w-100 mx-auto py-2 font-inter">
      <div className="row mx-auto w-100">
        <div className="col-md-12 w-100">
          <h3 className="text-3xl font-bold text-center mb-6 text-gray-800">Section</h3>

          <form
            onSubmit={handleSubmit}
            className="bg-white mb-8 max-w-4xl mx-auto p-4 rounded shadow"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Class Dropdown */}
              <div className="col-md-6">
                <div className="row mb-2">
                  <label className="block text-sm col-sm-4 font-medium text-gray-700 mb-1">
                    Select Class:
                  </label>
                  <div className="col-sm-8">
                    <select
                      value={selectedClassId || ""}
                      onChange={(e) => {
                        dispatch(setSelectedClassId(e.target.value));
                      }}
                      className="w-full p-2 form-control border border-gray-300 rounded-md"
                    >
                      <option value="">-- Select Class --</option>
                      {classes.map((cls) => (
                        <option key={cls.class_id} value={cls.class_id}>
                          {cls.class_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Subject Dropdown */}
              <div className="col-md-6">
                <div className="row mb-2">
                  <label className="block text-sm col-sm-4 font-medium text-gray-700 mb-1">
                    Select Subject:
                  </label>
                  <div className="col-sm-8">
                    <select
                      value={selectedSubjectId || ""}
                      onChange={(e) => dispatch(setSelectedSubjectId(e.target.value))}
                      className="w-full p-2 form-control border border-gray-300 rounded-md"
                      disabled={!selectedClassId || loadingSubjects}
                    >
                      <option value="">-- Select Subject --</option>
                      {subjects.map((sub) => (
                        <option key={sub.subject_id} value={sub.subject_id}>
                          {sub.subject_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Section Input Field */}
              <div className="col-md-12">
                <div className="row mb-2">
                  <label className="block text-sm col-sm-2 font-medium text-gray-700 mb-1">
                    Section Name:
                  </label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      value={sectionName}
                      onChange={(e) => setSectionName(e.target.value)}
                      placeholder="Enter Section Name"
                      className="w-full p-2 form-control border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="col-sm-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
                    >
                      {saving ? "Saving..." : editingSection ? "Update" : "Add"} Section
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Section List */}
      <div className="row mt-4 mx-auto w-100">
        <div className="col-md-12 w-100">
          <div className="bg-white max-w-4xl mx-auto p-4 rounded shadow">
            <h4 className="text-xl font-semibold mb-4 text-gray-800">Section List</h4>
            <TableComponent
              tableColumns={tableColumns}
              tableData={processSections(sections || [])}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionManager;
